import axios, { formatErrorResponse } from './axios-config';
import { addAuthorizationHeader, addTrackingHeader, buildApiEndpoint, getRequestBody } from '../helper-fns/apiHelper';
import {
	IApiErrorResponse,
	IBulkInsertItemsToCart,
	ICartApiResponse,
	ICartData,
	ICartMembershipRequest,
	IGetCartInfo,
	IInsertItemToCart,
	IRemoveCartItem,
	IUpdateCartItem,
	IUpsellApiResponse,
	IUpsellRequestBody,
	IUpsellData,
} from '../models';
import { validationErrorCodes } from '../constants';

export const removeCartSessionData = () => {
	sessionStorage.removeItem('cartHash');
	sessionStorage.removeItem('cartToken');
};

const handleApiException = (res: IApiErrorResponse) => {
	const { response } = res;
	const { errors, code } = formatErrorResponse(response);
	if (code === 401) {
		removeCartSessionData();
	}

	return { errors, code, redirectToErrorPage: code !== 401 && !validationErrorCodes.includes(errors[0].errorNumber) };
};

// Business logic for SUCCESSFUL http calls: data.code should be used
const handleApiResponse = ({ data }: ICartApiResponse): ICartData => ({ ...data });

const handleApiUpsellResponse = ({ data }: IUpsellApiResponse): IUpsellData => ({ ...data });

export const CartService = {
	insertItemToCart: async ({ body, token, lang }: IInsertItemToCart): Promise<ICartData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/cart/addItem`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then((res: ICartApiResponse) => handleApiResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
	bulkInsertItemsToCart: async ({ body, token, lang }: IBulkInsertItemsToCart): Promise<ICartData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/cart/addItems`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then((res: ICartApiResponse) => handleApiResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
	updateCartItem: async ({ body, token, lang }: IUpdateCartItem): Promise<ICartData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/cart/updateItem`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then((res: ICartApiResponse) => handleApiResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
	removeCartItem: async ({ body, token, lang }: IRemoveCartItem): Promise<ICartData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint(`/cart/removeItem`), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then((res: ICartApiResponse) => handleApiResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},

	getCartInfo: async ({ body, token, lang }: IGetCartInfo): Promise<ICartData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint('/cart/getInfo'), getRequestBody({ body, lang }), addTrackingHeader(config))
			.then((res: ICartApiResponse) => handleApiResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
	updateCartWithMembership: async (body: ICartMembershipRequest, token: string): Promise<ICartData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint('/cart/updateCartMemberYesNo'), getRequestBody({ body }), addTrackingHeader(config))
			.then((res: ICartApiResponse) => handleApiResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
	getUpsell: async (body: IUpsellRequestBody, token: string): Promise<IUpsellData> => {
		const config = addAuthorizationHeader(token);
		return await axios
			.post(buildApiEndpoint('/cart/getUpsell'), getRequestBody({ body }), addTrackingHeader(config))
			.then((res: IUpsellApiResponse) => handleApiUpsellResponse(res))
			.catch((res: IApiErrorResponse) => handleApiException(res));
	},
};
